import { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import {
  ClockMinor,
  HorizontalDotsMinor,
  MinusMinor,
  RefreshMinor,
  TickMinor,
} from '@shopify/polaris-icons'
import { CollectedDataStatus, collectedDataStatusTitle } from 'src/constants/collectedData'
import { GSIcon } from '../shared/GSIcon'

interface Props {
  status: CollectedDataStatus
  recollecting?: boolean
  isRequestApprove?: boolean
  expiresSoon?: boolean
}

const recollectingStatus = {
  color: colors.COLORS_ORANGE_500,
  text: 'Recollection',
  icon: <RefreshMinor width={20} fill={colors.COLORS_ORANGE_500} />,
}

const expiresSoonStatus = {
  color: colors.COLORS_ORANGE_500,
  text: 'Expires soon',
  icon: (
    <GSIcon iconName="gs_triangle_exclamation" css={tw`[&>path]:stroke-[rgba(255, 99, 32, 1)]`} />
  ),
}

const stepStatusMap = {
  [CollectedDataStatus.WaitingForApproval]: {
    color: colors.COLORS_ORANGE_500,
    text: collectedDataStatusTitle[CollectedDataStatus.WaitingForApproval],
    icon: <ClockMinor width={20} fill={colors.COLORS_ORANGE_500} />,
  },
  [CollectedDataStatus.Completed]: {
    color: colors.COLORS_GREEN_500,
    text: collectedDataStatusTitle[CollectedDataStatus.Completed],
    icon: <TickMinor width={20} fill={colors.COLORS_GREEN_500} />,
  },
  [CollectedDataStatus.Created]: {
    color: colors.SHADES_GREY_300,
    text: collectedDataStatusTitle[CollectedDataStatus.Created],
    icon: <MinusMinor width={20} fill={colors.SHADES_GREY_300} />,
  },
  [CollectedDataStatus.Failed]: {
    color: colors.COLORS_RED_500,
    text: collectedDataStatusTitle[CollectedDataStatus.Failed],
    icon: <HorizontalDotsMinor width={20} fill={colors.COLORS_RED_500} />,
  },
  [CollectedDataStatus.WaitingForInternalApproval]: {
    color: colors.SHADES_GREY_300,
    text: collectedDataStatusTitle[CollectedDataStatus.WaitingForInternalApproval],
    icon: <HorizontalDotsMinor width={20} fill={colors.SHADES_GREY_300} />,
  },
  [CollectedDataStatus.InProgress]: {
    color: colors.COLORS_BLUE_500,
    text: collectedDataStatusTitle[CollectedDataStatus.InProgress],
    icon: <HorizontalDotsMinor width={20} fill={colors.COLORS_BLUE_500} />,
  },
  [CollectedDataStatus.Expired]: {
    color: colors.COLORS_RED_500,
    text: collectedDataStatusTitle[CollectedDataStatus.Expired],
    icon: (
      <GSIcon iconName="gs_triangle_exclamation" css={tw`[&>path]:stroke-[rgba(255, 26, 81, 1)]`} />
    ),
  },
}

const ContractorStepStatusComponent = ({
  status,
  recollecting,
  isRequestApprove,
  expiresSoon,
}: Props) => {
  const onboardingStatus = expiresSoon ? expiresSoonStatus : stepStatusMap[status]
  const params = recollecting ? recollectingStatus : onboardingStatus

  if (!params) {
    return null
  }

  return (
    <div css={tw`flex items-center`}>
      {params.icon}
      <Typography css={tw`ml-1`} variant="body2" color={params.color}>
        {status === CollectedDataStatus.Completed && isRequestApprove
          ? `${params.text} by MC`
          : params.text}
      </Typography>
    </div>
  )
}

export const ContractorStepStatus = memo(ContractorStepStatusComponent)
